import fetch, { RequestInit } from "node-fetch";
import { z, ZodSchema, ZodTypeDef } from "zod";
import { RequestExecutorError } from "./request-executor-error";

export type RequestExecutorConfig = {
  host: string;
  accessToken?: string;
};

export type RequestExecutorJSONRequest = Omit<RequestInit, "body"> & {
  body?: object;
  query?: URLSearchParams;
};

const ResponseWrapper = z.object({
  result: z.record(z.any()),
});

export class RequestExecutor {
  private readonly testServerConfig: RequestExecutorConfig;

  constructor(testServerConfig: RequestExecutorConfig) {
    this.testServerConfig = testServerConfig;
  }

  async json<O, I>(
    path: `/${string}`,
    schema: ZodSchema<O, ZodTypeDef, I>,
    requestInit: RequestExecutorJSONRequest = {}
  ) {
    // Headers
    const defaultHeaders: Record<string, string> = {
      Accept: "application/json",
    };

    if (requestInit.body) defaultHeaders["Content-Type"] = "application/json";
    if (this.testServerConfig.accessToken) {
      defaultHeaders["Authorization"] = `Bearer ${this.testServerConfig.accessToken}`;
    }

    // Body
    let body: string | undefined;
    if (requestInit.body && typeof requestInit.body === "object") body = JSON.stringify(requestInit.body);

    const response = await fetch(this.fullUrl(path, requestInit.query), {
      ...requestInit,
      headers: {
        ...requestInit.headers,
        ...defaultHeaders,
      },
      body: body,
    });

    const endpoint = `${requestInit.method ?? "GET"} ${path}`;

    if (!response.ok) {
      const bodyString = await response.text();
      throw RequestExecutorError.serverError(endpoint, response.status, bodyString);
    }

    const json = await response.json();
    const wrapperParseResult = ResponseWrapper.safeParse(json);
    if (!wrapperParseResult.success) {
      console.error(`Response was:`);
      console.error(json);
      throw RequestExecutorError.responseParsingError(endpoint, wrapperParseResult.error);
    }

    const responseParseResult = schema.safeParse(wrapperParseResult.data.result);
    if (!responseParseResult.success) {
      console.error(`Response was:`);
      console.error(wrapperParseResult.data.result);
      throw RequestExecutorError.responseParsingError(endpoint, responseParseResult.error);
    }

    return responseParseResult.data;
  }

  private fullUrl(path: string, query: URLSearchParams | undefined): string {
    let url = `https://${this.testServerConfig.host}/api/v2${path}`;

    const queryString = query?.toString();
    if (queryString) url += `?${queryString}`;

    return url;
  }
}
